import { Link } from '@ipfabric/ui-components/Link/Link';
import {
  type NodeSn,
  type TableColumnFormatHostnameObject,
  getValueFromKeyOfType,
} from '@ipfabric/utils';
import type { RenderedTableCell } from 'components/Table/cells/RenderedTableCell';
import {
  ARRAY_CELL_MAX_ITEMS,
  maybeWrapWithMore,
} from 'components/Table/cells/helpers/trimLargeArray';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/lib/function';
import type { ReactNode } from 'react';
import { unsafeWrapWithReactKeyByIndex } from 'utils/react/unsafeWrapWithReactKeyByIndex';
import type { RenderCellContentArguments } from '../model/TableCellContentRenderFunction';

export const renderHostnameObjectCell = <TRow,>(
  format: TableColumnFormatHostnameObject<TRow, unknown>,
  { row, context, severity }: RenderCellContentArguments<TRow>,
): RenderedTableCell => {
  const cell = getValueFromKeyOfType(row, format.field);

  const renderChild = (
    sn: NodeSn | null,
    hostname: string | null,
  ): ReactNode =>
    !hostname ||
    !sn ||
    sn === 'unknown' ||
    !context.onOpenExplorer ||
    context.disableLinks ? (
      hostname
    ) : (
      <Link
        renderAs='button'
        accent={severity === null ? 'positive' : 'contrast'}
        onClick={() => {
          context.onOpenExplorer!(row, {
            sources: [{ hostname, kind: 'device', sn }],
          });
        }}
      >
        {hostname}
      </Link>
    );

  const content = Array.isArray(cell)
    ? pipe(
        cell,
        (a) => a.slice(0, ARRAY_CELL_MAX_ITEMS),
        array.map((c) => renderChild(c[format.snProp], c[format.hostnameProp])),

        (a) => a.map(unsafeWrapWithReactKeyByIndex),
        array.reduce<ReactNode, ReactNode | null>(null, (prev, current) =>
          prev === null ? [current] : [prev, ', ', current],
        ),
        maybeWrapWithMore(cell),
      )
    : renderChild(
        getValueFromKeyOfType(cell, format.hostnameProp),
        getValueFromKeyOfType(cell, format.snProp),
      );

  return {
    content,
  };
};
